import React, { useState, useRef, useCallback, useEffect } from "react";
import { DropDownSelect } from "../DropDownSelect";
import { components } from "react-select";
import { IconSearch } from "@tabler/icons-react";
import API from "../../utilities/LocalApiProxy";
import { searchBoxQuery } from "../../actions/searchBoxActions";
import { connect } from "react-redux";
import {
	closeGlobalDialog,
	openGlobalDialog,
} from "../../actions/globalEditDialogActions";
import { createNewDocumentFromTemplate } from "../../actions/newDocumentActions";
import {
	createNewDocumentSubmitHandler,
	createNewDocumentValidationHandler,
} from "../mandatoryProfiling/dialog/NewDocumentWithProfilingDialog";
import {
	dividerOption,
	formatClientOptions,
	formatDocumentOptions,
	formatMatterOptions,
	getSettingsOptions,
	SearchBoxOptionsTypeMap,
	searchOptionsNoSearchString,
	searchOptionsWithSearchString,
} from "./SearchBoxUtils";
import { getDisabledStateFromLifecycleStatuses } from "../../utilities/lifecycleStatus";

function SearchBox(props) {
	const {
		searchUrlAfterQuery,
		searchMenuOptions,
		placeholder,
		recentMatterIDs,
		debounceTimeMS = 500,
		hasPermissionsAccess,
		shouldShowCreateDocument,
		allowCreateClient,
		allowCreateMatter,
	} = props;
	const [query, setQuery] = useState("");
	const debounceTimeout = useRef();
	// bumped to invalidate a pending/in-flight /searchBox lookup
	const searchBoxRequestID = useRef(0);
	// resolve() for the pending loadOptions promise, so we can settle it
	// immediately when cancelling
	const pendingResolve = useRef(null);

	const leaveSearchBoxDropdown = () => {
		clearTimeout(debounceTimeout.current);
		searchBoxRequestID.current += 1;
		if (pendingResolve.current) {
			pendingResolve.current([]);
			pendingResolve.current = null;
		}
		// close the dropdown when we leave the search box
		document.activeElement?.blur();
	};

	useEffect(() => {
		return () => {
			clearTimeout(debounceTimeout.current);
			pendingResolve.current?.([]);
		};
	}, []);

	const navigateToSearch = (querystring) => {
		leaveSearchBoxDropdown();
		const url = `/search/${Math.floor(
			Math.random() * 10000
		)}?q=${query}${querystring || searchUrlAfterQuery}`;
		setQuery("");
		window.globalHistory.push(url);
	};
	const navigateToMatter = (matterID) => {
		const url = `/matters/${matterID}/docs/`;
		setQuery("");
		window.globalHistory.push(url);
	};
	const navigateToClientDetail = (clientID) => {
		const url = `/clients/${clientID}`;
		setQuery("");
		window.globalHistory.push(url);
	};

	const fetchSearchResults = useCallback(
		(searchValue) => {
			return searchBoxQuery({
				query: searchValue,
				recentMatterIDs: recentMatterIDs || [],
			});
		},
		[
			searchMenuOptions,
			recentMatterIDs,
			allowCreateClient,
			allowCreateMatter,
			hasPermissionsAccess,
		]
	);

	const debouncedApiCall = useCallback(
		(inputValue) => {
			return new Promise((resolve) => {
				// use a debounce timer so that the async call
				// (eg a request to a server) doesn't execute
				// on every keystroke
				clearTimeout(debounceTimeout.current);
				const requestID = ++searchBoxRequestID.current;
				pendingResolve.current = resolve;

				debounceTimeout.current = setTimeout(() => {
					fetchSearchResults(inputValue).then((response) => {
						if (requestID !== searchBoxRequestID.current) {
							// a newer request (or navigation) superseded this one
							return;
						}
						pendingResolve.current = null;
						// API returns filtered matters, clients and documents (up to 3 each)
						// Format them for the dropdown display
						const matterOptions = formatMatterOptions(response);
						const clientOptions = formatClientOptions(response);
						const documentOptions = formatDocumentOptions(response);

						// determine if we have any settings matches
						const settingsMatches = getSettingsOptions({
							inputValue,
							hasPermissionsAccess,
							launcherURL: props.launcherURL,
							userRights: props.userRights,
						});

						// Combine search options with the filtered results
						const searchOptions1 = inputValue
							? searchOptionsWithSearchString({
									searchString: inputValue,
									searchMenuOptions,
								})
							: searchOptionsNoSearchString({
									searchMenuOptions,
								});
						let searchOptions = [...searchOptions1];

						// check if we have any results to show
						// add divider only if we have results
						if (
							documentOptions.length ||
							matterOptions.length ||
							clientOptions.length
						) {
							searchOptions = searchOptions
								.concat(dividerOption)
								.concat(documentOptions)
								.concat(matterOptions)
								.concat(clientOptions);
						}

						// Add create options if applicable
						if (inputValue) {
							let shouldCreateDivider = true;

							if (allowCreateMatter) {
								if (shouldCreateDivider) {
									shouldCreateDivider = false;
									searchOptions.push(dividerOption);
								}
								searchOptions.push({
									label: `Create New Matter "${inputValue}"`,
									type: "createMatter",
								});
							}
							if (allowCreateClient) {
								if (shouldCreateDivider) {
									shouldCreateDivider = false;
									searchOptions.push(dividerOption);
								}
								searchOptions.push({
									label: `Create New Client "${inputValue}"`,
									type: "createClient",
								});
							}
							if (shouldShowCreateDocument) {
								if (shouldCreateDivider) {
									shouldCreateDivider = false;
									searchOptions.push(dividerOption);
								}
								searchOptions.push({
									label: `Create New Document "${inputValue}.docx"`,
									type: "createDocument",
								});
							}
						}

						if (settingsMatches.length > 0) {
							searchOptions = searchOptions
								.concat(dividerOption)
								.concat(settingsMatches);
						}

						resolve(searchOptions);
					});
				}, debounceTimeMS);
			});
		},
		[debounceTimeMS, fetchSearchResults]
	);
	const openNewDocumentDialog = (extension) => {
		const directoryID = props.directoryID;
		// Show loading dialog
		props.openGlobalDialog({
			label: "Loading...",
			hideTextfield: true,
			hideCancelButton: true,
			readOnly: true,
			mainText: "Checking document requirements...",
		});

		// Check mandatory profiling requirements
		API.post("/document/checkMandatoryProfilingForNewDocument", {
			body: {
				directoryID,
				extension,
			},
		})
			.then((res) => {
				// Close loading dialog
				props.closeGlobalDialog();

				const {
					stronglySuggestProfiling,
					requiredFields,
					availableOptions,
				} = res.mysql;

				// Always use the same dialog, pass promptForProfiling to control rendering
				props.openGlobalDialog(
					{
						type: "NewDocumentWithMandatoryProfiling",
						label: `Create New Word Document`,
						placeHolder: `New Document`,
						value: query,
						submitButtonLabel: "Create and Open Document",
						newDocumentProfilingConfig: {
							stronglySuggestProfiling,
							requiredFields,
							availableOptions,
							extension,
						},
					},
					createNewDocumentSubmitHandler({
						extension,
						createNewDocumentFromTemplate:
							props.createNewDocumentFromTemplate,
						directoryID: props.directoryID,
						cognitoSub: props.cognitoSub,
					}),
					createNewDocumentValidationHandler({
						extension,
						directoryID,
						documentList: props.documentList,
						requiredFields: requiredFields,
					})
				);
			})
			.catch((err) => {
				props.closeGlobalDialog();
				console.error("Error checking mandatory profiling:", err);
			});
	};

	const handleClick = (key) => {
		// selecting an option navigates away or opens a dialog, so the
		// searchBox lookup is no longer needed
		leaveSearchBoxDropdown();
		if (key?.type === "matter") {
			navigateToMatter(key.value);
		} else if (key?.type === "client") {
			navigateToClientDetail(key.value);
		} else if (key?.type === "search") {
			navigateToSearch(key.value);
		} else if (key?.type === "createMatter") {
			const url = `/matters/new?name=${encodeURIComponent(query)}`;
			setQuery("");
			window.globalHistory.push(url);
		} else if (key?.type === "createClient") {
			const url = `/clients/new?name=${encodeURIComponent(query)}`;
			setQuery("");
			window.globalHistory.push(url);
		} else if (key?.type === "createDocument") {
			openNewDocumentDialog("docx");
		} else if (key?.type === "settings") {
			const url = key.value;
			setQuery("");
			window.globalHistory.push(url);
		} else if (key?.type === "downloadLauncher") {
			// direct download
			window.location.href = key.value;
		} else if (key?.type === "document") {
			const url = `/docs/${key.value}`;
			setQuery("");
			window.globalHistory.push(url);
		}
	};

	const filterOptions = (candidate) => {
		return !candidate.data.__isNew__;
	};

	const Option = (props) => {
		const { iconComponent } = SearchBoxOptionsTypeMap[props.data.type];

		const dividerStyleProps = {
			padding: "2px 0px",
			width: "100%",
		};
		const newInnerProps =
			props.data.type !== "divider"
				? { ...props.innerProps }
				: { ...props.innerProps, style: dividerStyleProps };
		return (
			<div
				className="globalSearchboxOption"
				style={{ position: "relative" }}
			>
				<span>{iconComponent}</span>
				<components.Option {...props} innerProps={newInnerProps} />
			</div>
		);
	};
	const DropdownIndicator = (props) => {
		return (
			components.DropdownIndicator && (
				<components.DropdownIndicator {...props}>
					<IconSearch size={16} style={{ verticalAlign: "sub" }} />
				</components.DropdownIndicator>
			)
		);
	};
	// keeps a "Search LexWorkplace for X" option clickable while results are
	// still loading
	const MenuList = (menuListProps) => {
		const { isLoading } = menuListProps.selectProps;
		return (
			<components.MenuList {...menuListProps}>
				{isLoading && query
					? searchOptionsWithSearchString({
							searchString: query,
							searchMenuOptions,
						}).map((option) => (
							<div
								key={option.label}
								className="globalSearchboxOption"
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => navigateToSearch(option.value)}
								style={{
									position: "relative",
									cursor: "pointer",
								}}
							>
								<span>
									{
										SearchBoxOptionsTypeMap.search
											.iconComponent
									}
								</span>
								<div
									style={{
										// padding-left comes from .globalSearchboxOption > div in App.css
										paddingTop: 8,
										paddingRight: 12,
										paddingBottom: 8,
										wordBreak: "break-word",
									}}
								>
									{option.label}
								</div>
							</div>
						))
					: null}
				{menuListProps.children}
			</components.MenuList>
		);
	};
	const getTextboxValue = () => {
		return query ? { value: query, label: query } : null;
	};
	const getDefaultSearchDropdown = () => {
		return searchOptionsNoSearchString({ searchMenuOptions });
	};
	const loadOptions = (inputValue) => {
		// Use debounced API call instead of local filtering
		return debouncedApiCall(inputValue);
	};

	return (
		<div
			style={{
				minWidth: 340,
				position: "relative",
				top: 2,
			}}
		>
			<div
				style={{
					top: -24,
					position: "absolute",
					zIndex: 151,
					width: "100%",
					height: 28,
					textAlign: "left",
				}}
			>
				<div
					style={{
						lineHeight: 1,
						height: 30,
					}}
				>
					<DropDownSelect
						label="Search"
						name="search"
						placeholder={placeholder}
						isClearable
						isCreatable
						isAsync
						isDisabled={false}
						defaultValue={null}
						components={{ Option, DropdownIndicator, MenuList }}
						onKeyDown={(e) => {
							e.key === "Enter" && navigateToSearch();
							if (e.key === " " && !query) {
								// prevents selecting the first option when
								// space is pressed with empty search box
								e.preventDefault();
							}
						}}
						loadingMessage={() => "Thinking..."}
						value={getTextboxValue()}
						onInputChange={setQuery}
						defaultOptions={getDefaultSearchDropdown()}
						handler={handleClick}
						loadOptions={loadOptions}
						filterOption={filterOptions}
						classNamePrefix={"globalSearchbox"}
						styles={{
							menu: (base) => ({ ...base, width: "600px" }),
							menuList: (base) => ({
								...base,
								maxHeight: "calc(100vh - 90px)",
								overflowY: "auto",
							}),
						}}
					/>
				</div>
			</div>
		</div>
	);
}

class SearchBoxWrapper extends React.Component {
	render() {
		return (
			<SearchBox
				placeholder={this.props.searchPlaceholder}
				searchUrlAfterQuery={this.props.searchUrlAfterQuery}
				searchMenuOptions={this.props.searchMenuOptions}
				recentMatterIDs={this.props.recentMatterIDs}
				hasPermissionsAccess={this.props.hasPermissionsAccess}
				shouldShowCreateDocument={this.props.shouldShowCreateDocument}
				allowCreateClient={this.props.allowCreateClient}
				allowCreateMatter={this.props.allowCreateMatter}
				directoryID={this.props.directoryID}
				cognitoSub={this.props.cognitoSub}
				documentList={this.props.documentList}
				openGlobalDialog={this.props.openGlobalDialog}
				closeGlobalDialog={this.props.closeGlobalDialog}
				createNewDocumentFromTemplate={
					this.props.createNewDocumentFromTemplate
				}
				launcherURL={this.props.launcherURL}
				userRights={this.props.userRights}
			/>
		);
	}
}

const mapStateToProps = (state) => {
	const matterOrFolderLifecycleStatusObject =
		getDisabledStateFromLifecycleStatuses({
			matterLifecycleStatus: state.document.matterLifecycleStatus,
			folderLifecycleStatus: state.document.folderLifecycleStatus,
		});
	const shouldShowCreateDocument =
		state.currentPage.creatableContext &&
		(state.document.currentRoot.indexOf("docs/") === 0 ||
			state.document.currentRoot.indexOf("public/") === 0 ||
			state.document.currentRoot.indexOf("private/") === 0) &&
		!matterOrFolderLifecycleStatusObject.disabled;

	return {
		allowCreateClient: state.user.userRights.CREATE_CLIENT_ALLOWED,
		allowCreateMatter: state.user.userRights.CREATE_MATTER_ALLOWED,
		searchPlaceholder: state.globalSearchbar.searchPlaceholder,
		searchUrlAfterQuery: state.globalSearchbar.searchUrlAfterQuery,
		searchMenuOptions: state.globalSearchbar.menuOptions,
		recentMatterIDs: state.browserLocalstorage.recentlyViewedMatters,
		hasPermissionsAccess: state.user.hasPermissionsAccess,
		shouldShowCreateDocument: shouldShowCreateDocument,
		directoryID: state.document.directoryID,
		cognitoSub: state.user.cognitoUser.attributes.sub,
		documentList: state.document.documentList,
		launcherURL: state.global.launcherURL,
		userRights: state.user.userRights,
	};
};
export default connect(mapStateToProps, {
	openGlobalDialog,
	closeGlobalDialog,
	createNewDocumentFromTemplate,
})(SearchBoxWrapper);
